import React from 'react';

/**
 * CalendarEvent - Custom event renderer for react-big-calendar
 * Shows appointment details with technician color coding
 */

const STATUS_DOTS = {
  accepted: 'bg-green-500',
  confirmed: 'bg-green-500',
  pending: 'bg-amber-500',
  scheduled: 'bg-amber-500',
  completed: 'bg-blue-500',
  in_progress: 'bg-blue-400',
  cancelled: 'bg-red-500',
  cancelled_by_seller: 'bg-red-500',
  cancelled_by_buyer: 'bg-red-500',
  no_show: 'bg-orange-500',
  declined: 'bg-red-400',
};

const formatTime = (date) => {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Full event component for week/day views - shows all details
 */
export const CalendarEventFull = ({ event }) => {
  // Day off block
  if (event.isDayOff) {
    return (
      <div className="px-1 py-0.5 text-[10px] text-red-700 italic truncate">
        {event.title}
      </div>
    );
  }

  // Grouped ALF visit
  if (event.isAlfGroup) {
    const group = event.alfGroup;
    return (
      <div
        className="h-full px-1.5 py-0.5 rounded-sm overflow-hidden bg-purple-50"
        style={{ borderLeft: `3px solid ${event.techColor}` }}
      >
        <div className="flex items-center gap-1 mb-0.5">
          <span className="inline-block w-1.5 h-1.5 rounded-sm flex-shrink-0 bg-purple-500" />
          <span className="font-semibold text-gray-900 truncate text-[11px]">
            {group.clientName}
          </span>
        </div>
        <div className="text-[10px] text-purple-700 font-medium">
          {group.count} resident{group.count !== 1 ? 's' : ''} &middot; ALF
        </div>
        {group.totalRevenue > 0 && (
          <div className="text-[9px] text-gray-500">
            ${group.totalRevenue.toFixed(0)}
          </div>
        )}
      </div>
    );
  }

  // Regular appointment
  const appt = event.appointment;
  const statusClass = STATUS_DOTS[appt.status?.toLowerCase()] || 'bg-gray-400';

  return (
    <div
      className={`h-full px-1.5 py-0.5 rounded-sm overflow-hidden ${
        appt.appointment_type === 'Event' ? 'bg-blue-50' : 'bg-white'
      }`}
      style={{ borderLeft: `3px solid ${event.techColor}` }}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusClass}`} />
        <span className="font-semibold text-gray-900 truncate text-[11px]">
          {formatTime(event.start)} {appt.client_name}
        </span>
      </div>
      <div className="text-[10px] text-gray-500 truncate">
        {appt.technician_name}
        {(appt.city || appt.zip_code) && (
          <span className="text-gray-400"> · {appt.city || ''}{appt.city && appt.zip_code ? ' ' : ''}{appt.zip_code || ''}</span>
        )}
      </div>
      {appt.appointment_type === 'Event' && (
        <span className="text-[9px] font-semibold text-blue-600 uppercase tracking-wide">
          Event
        </span>
      )}
    </div>
  );
};

export default CalendarEventFull;
